import React from 'react';
import {
  Button,
  Card,
  Typography,
  Spin,
  Row,
  Col,
  List,
  Tag,
  Alert,
  Calendar,
  DatePicker,
  Switch
} from 'antd';
import { CalendarOutlined, WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import { COLORS } from '../../../constants/colors';
import { CalendarWrapper } from '../styled';
import { formatDuration } from '../constants';

const { Text } = Typography;

const Step1EventSelection = ({
  loading,
  eventTypes,
  selectedEventType,
  selectedDate,
  selectedEndDate,
  selectedTimeSlot,
  availableSlots,
  loadingSlots,
  isSelectedDateBlocked,
  handleEventTypeSelect,
  onDateSelect,
  handleEndDateSelect,
  handleTimeSlotSelect,
  dateCellRender
}) => {
  // When an event type is selected, smoothly bring the date calendar (the
  // next step) into view so the user knows where to go next.
  const dateSectionRef = React.useRef(null);
  React.useEffect(() => {
    if (selectedEventType && dateSectionRef.current) {
      const id = setTimeout(() => {
        dateSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return () => clearTimeout(id);
    }
  }, [selectedEventType]);

  return (
  <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px' }}>
    <Alert
      message="Admin Booking Creation"
      description="Time slots shown are currently available. If a slot becomes unavailable during booking, you'll be notified."
      type="info"
      showIcon
      closable
      style={{ marginBottom: '16px' }}
    />

    {/* Event Type Selection */}
    <div style={{ marginBottom: '24px' }}>
      <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
        Select Event Type
      </Text>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin />
        </div>
      ) : (
        <Row gutter={[12, 12]}>
          {eventTypes.map((eventType) => (
            <Col span={24} key={eventType._id}>
              <Card
                hoverable
                onClick={() => handleEventTypeSelect(eventType)}
                style={{
                  borderColor: selectedEventType?._id === eventType._id ? COLORS.$primaryBlue : '#d9d9d9',
                  borderWidth: selectedEventType?._id === eventType._id ? '2px' : '1px',
                  background: selectedEventType?._id === eventType._id ? `${COLORS.$primaryBlue}08` : 'white'
                }}
                bodyStyle={{ padding: '16px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong style={{ fontSize: '15px' }}>{eventType.name}</Text>
                    {eventType.description && (
                      <div style={{ color: '#8c8c8c', fontSize: '13px', marginTop: '4px' }}>
                        {eventType.description}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Tag color="blue">{formatDuration(eventType.duration)}</Tag>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>

    {/* Date Selection */}
    {selectedEventType && (
      <div ref={dateSectionRef} style={{ marginBottom: '24px', scrollMarginTop: '8px' }}>
        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
          Select Date
        </Text>
        <div style={{ color: '#666', fontSize: '12px', marginBottom: '12px' }}>
          Dates with a red background are blocked. Past dates are disabled. Admins can still book on blocked dates.
        </div>
        <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#1a1a1a' }}>
          {selectedDate ? selectedDate.format('MMMM YYYY') : moment().format('MMMM YYYY')}
        </div>
        <CalendarWrapper>
          <Calendar
            fullscreen={false}
            mode="month"
            dateCellRender={dateCellRender}
            onSelect={onDateSelect}
            disabledDate={(date) => {
              const isPast = date.isBefore(moment(), 'day');
              return isPast;
            }}
          />
        </CalendarWrapper>

        {/* Multi-Day Booking Option */}
        {selectedDate && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px 16px', 
            background: '#f6f8fa', 
            borderRadius: '8px',
            border: '1px solid #e8e8e8'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: selectedEndDate ? '12px' : '0' }}>
              <div>
                <Text strong style={{ fontSize: '13px' }}>Multi-Day Event</Text>
                <div style={{ color: '#666', fontSize: '12px' }}>
                  Enable if this booking spans multiple days
                </div>
              </div>
              <Switch
                size="small"
                checked={!!selectedEndDate}
                onChange={(checked) => {
                  if (!checked) {
                    handleEndDateSelect(null);
                  } else {
                    // Default to next day
                    handleEndDateSelect(selectedDate.clone().add(1, 'day'));
                  }
                }}
              />
            </div>
            {selectedEndDate && (
              <div>
                <Text style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '8px' }}>
                  End Date (last day of the event):
                </Text>
                <DatePicker
                  value={selectedEndDate}
                  onChange={(date) => handleEndDateSelect(date)}
                  disabledDate={(date) => {
                    return !date || !date.isAfter(selectedDate, 'day');
                  }}
                  style={{ width: '100%' }}
                  format="MMMM D, YYYY"
                />
                <div style={{ 
                  marginTop: '8px', 
                  padding: '8px 12px', 
                  background: '#e6f7ff', 
                  borderRadius: '6px',
                  border: '1px solid #91d5ff',
                  fontSize: '12px',
                  color: '#1890ff'
                }}>
                  <CalendarOutlined style={{ marginRight: '6px' }} />
                  This booking will span <strong>{selectedEndDate.diff(selectedDate, 'days') + 1} days</strong>: {selectedDate.format('MMM D')} – {selectedEndDate.format('MMM D, YYYY')}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )}

    {/* Time Slot Selection */}
    {selectedDate && (
      <div style={{
        ...(isSelectedDateBlocked ? {
          border: '2px solid #ff4d4f',
          borderRadius: '8px',
          padding: '12px',
          background: 'rgba(255, 77, 79, 0.03)'
        } : {})
      }}>
        <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: '12px' }}>
          Select Time Slot
          {isSelectedDateBlocked && (
            <span style={{ 
              marginLeft: '8px', 
              color: '#ff4d4f', 
              fontWeight: 'normal',
              fontSize: '12px'
            }}>
              (Blocked Date Override)
            </span>
          )}
        </Text>
        {isSelectedDateBlocked && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: '12px' }}
            message="This date is blocked"
            description="You are booking on a blocked date. This will be logged as an admin override."
          />
        )}
        {loadingSlots ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : availableSlots.length > 0 ? (
          <>
            <div style={{ 
              marginBottom: '12px', 
              display: 'flex', 
              gap: '16px', 
              alignItems: 'center',
              fontSize: '12px',
              color: '#666'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '2px', 
                  background: '#fff', 
                  border: '1px solid #d9d9d9' 
                }} />
                Available
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ 
                  width: '12px', 
                  height: '12px', 
                  borderRadius: '2px', 
                  background: '#fff7e6', 
                  border: '1px solid #ffa940' 
                }} />
                Booked (double-book available)
              </span>
            </div>
            <List
              grid={{ gutter: 8, column: 3 }}
              dataSource={availableSlots}
              renderItem={(slot) => {
                const isSelected = selectedTimeSlot?.startTime === slot.startTime;
                const isBooked = slot.isBooked;
                
                return (
                  <List.Item>
                    <div style={{ position: 'relative' }}>
                      <Button
                        block
                        onClick={() => handleTimeSlotSelect(slot)}
                        type={isSelected ? 'primary' : 'default'}
                        icon={isBooked ? <WarningOutlined /> : <CalendarOutlined />}
                        style={{
                          background: isSelected 
                            ? undefined 
                            : isBooked 
                              ? '#fff7e6' 
                              : undefined,
                          borderColor: isSelected 
                            ? undefined 
                            : isBooked 
                              ? '#ffa940' 
                              : undefined,
                          color: isSelected 
                            ? undefined 
                            : isBooked 
                              ? '#d46b08' 
                              : undefined,
                        }}
                      >
                        {moment(slot.startTime).format('h:mm A')}
                      </Button>
                      {isBooked && slot.conflictingBookings?.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-6px',
                          background: '#fa8c16',
                          color: 'white',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          fontSize: '11px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          border: '2px solid white'
                        }}>
                          {slot.conflictingBookings.length}
                        </div>
                      )}
                    </div>
                  </List.Item>
                );
              }}
            />
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: '#8c8c8c' }}>
            No available time slots for this date
          </div>
        )}
      </div>
    )}
  </div>
  );
};

export default Step1EventSelection;
